import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, Stack, useTheme } from "@mui/material";
import { Button, FoodsIcon, FoodsToggleButton } from "@foodpilot/foods";
import { MediaItem } from "src/pages/Settings/MediaLibrary/context";
import { MediaManagerDialog } from "src/components/MediaManager/MediaManagerDialog";
import { NativePuckTitleField } from "./NativePuckTitleField";

export type MediaManagerFieldProps = {
    onChange: (value: string) => void;
    value?: string;
    label?: string;
    allowedExtensions?: string[];
    allowedMimeTypes?: string[];
    placeholder?: string;
};

type TabValue = "url" | "media";

export const MediaManagerField = (props: MediaManagerFieldProps) => {
    const {
        onChange,
        value = "",
        label = "Media URL",
        allowedExtensions,
        allowedMimeTypes,
        placeholder = "Enter URL or select from media library",
    } = props;

    const { t } = useTranslation();
    const theme = useTheme();
    const [activeTab, setActiveTab] = useState<TabValue>("url");
    const [isMediaDialogOpen, setIsMediaDialogOpen] = useState(false);
    const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);

    const handleUrlChange = (newValue: string) => {
        onChange(newValue);
        setSelectedMedia(null);
    };

    const validateUrlMimeType = (url: string): boolean => {
        if (!allowedMimeTypes || allowedMimeTypes.length === 0 || !url) return true;

        const fileExtension = url.split(".").pop()?.toLowerCase();
        if (!fileExtension) return true;

        const extensionToMimeType: Record<string, string> = {
            jpg: "image/jpeg",
            jpeg: "image/jpeg",
            png: "image/png",
            gif: "image/gif",
            webp: "image/webp",
            svg: "image/svg+xml",
            mp4: "video/mp4",
            webm: "video/webm",
            mov: "video/quicktime",
            pdf: "application/pdf",
            doc: "application/msword",
            docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            xls: "application/vnd.ms-excel",
            xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            csv: "text/csv",
            txt: "text/plain",
        };

        const guessedMimeType = extensionToMimeType[fileExtension];
        if (!guessedMimeType) return true;

        return allowedMimeTypes.some((allowedType) => {
            if (allowedType.endsWith("/*")) {
                const baseType = allowedType.replace("/*", "");
                return guessedMimeType.startsWith(baseType);
            }
            return guessedMimeType === allowedType;
        });
    };

    const handleOpenMediaDialog = () => {
        setIsMediaDialogOpen(true);
    };

    const handleCloseMediaDialog = () => {
        setIsMediaDialogOpen(false);
    };

    const handleMediaSelect = (media: MediaItem) => {
        setSelectedMedia(media);
        onChange(media.url || "");
    };

    const handleClearSelection = () => {
        onChange("");
        setSelectedMedia(null);
    };

    const isMediaSelected = selectedMedia && value;

    return (
        <Box>
            <NativePuckTitleField label={label} icon="edit" />

            <Box
                sx={{
                    mb: theme.spacing(2),
                    width: "100%",
                    "& .MuiBox-root": { flexDirection: "column" },
                }}
            >
                <FoodsToggleButton
                    buttons={[
                        {
                            id: "url",
                            label: t("puck.media_manager.tab_url", "Custom URL"),
                            startIcon: "informations",
                            onClick: () => setActiveTab("url"),
                        },
                        {
                            id: "media",
                            label: t("puck.media_manager.tab_media", "Media Library"),
                            startIcon: "repositories",
                            onClick: () => setActiveTab("media"),
                        },
                    ]}
                    initialSelected={{
                        id: activeTab,
                        label:
                            activeTab === "media" ?
                                t("puck.media_manager.tab_media", "Media Library")
                            :   t("puck.media_manager.tab_url", "Custom URL"),
                        startIcon: activeTab === "media" ? "repositories" : "informations",
                        onClick: () => {},
                    }}
                />
            </Box>

            {activeTab === "url" && (
                <Stack spacing={2}>
                    <Box>
                        <input
                            type="text"
                            value={value}
                            onChange={(e) => handleUrlChange(e.target.value)}
                            placeholder={placeholder}
                            style={{
                                width: "100%",
                                padding: "8px 12px",
                                border: `1px solid ${!value || validateUrlMimeType(value) ? "var(--puck-color-grey-08)" : theme.palette.error.main}`,
                                borderRadius: "4px",
                                fontSize: "14px",
                                fontFamily: "var(--puck-font-family)",
                                backgroundColor: "var(--puck-color-white)",
                                color: "var(--puck-color-black)",
                                outline: "none",
                                boxSizing: "border-box",
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor =
                                    !value || validateUrlMimeType(value) ?
                                        "var(--puck-color-azure-04)"
                                    :   theme.palette.error.main;
                                e.target.style.boxShadow =
                                    !value || validateUrlMimeType(value) ?
                                        "0 0 0 2px var(--puck-color-azure-09)"
                                    :   `0 0 0 2px ${theme.palette.error.light}`;
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor =
                                    !value || validateUrlMimeType(value) ?
                                        "var(--puck-color-grey-08)"
                                    :   theme.palette.error.main;
                                e.target.style.boxShadow = "none";
                            }}
                        />
                        {value && !validateUrlMimeType(value) && (
                            <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
                                {t(
                                    "puck.media_manager.invalid_file_type",
                                    "This file type doesn't match the allowed types",
                                )}
                            </Typography>
                        )}
                        {allowedMimeTypes && allowedMimeTypes.length > 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                                {t("puck.media_manager.allowed_types", "Allowed types: {{types}}", {
                                    types: allowedMimeTypes.join(", "),
                                })}
                            </Typography>
                        )}
                    </Box>
                    {value && (
                        <Button
                            variant="ternary"
                            size="small"
                            onClick={handleClearSelection}
                            startIcon={<FoodsIcon icon="close" size={2} />}
                        >
                            {t("puck.media_manager.action.clear_selection", "Clear")}
                        </Button>
                    )}
                </Stack>
            )}

            {activeTab === "media" && (
                <Stack spacing={theme.spacing(2)}>
                    <Box
                        sx={{
                            border: `1px dashed ${theme.palette.divider}`,
                            borderRadius: theme.spacing(1),
                            p: theme.spacing(2),
                            textAlign: "center",
                            cursor: "pointer",
                            "&:hover": {
                                backgroundColor: theme.palette.action.hover,
                            },
                        }}
                        onClick={handleOpenMediaDialog}
                    >
                        {isMediaSelected ?
                            <Stack spacing={1} alignItems="center">
                                <FoodsIcon icon="check" size={3} />
                                <Typography
                                    variant="body2"
                                    fontWeight="medium"
                                    sx={{
                                        lineClamp: 1,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        maxWidth: "100%",
                                    }}
                                >
                                    {selectedMedia?.name}
                                </Typography>
                                <Typography variant="caption">
                                    {t("puck.media_manager.click_to_change", "Click to change selection")}
                                </Typography>
                            </Stack>
                        :   <Stack spacing={1} alignItems="center">
                                <FoodsIcon icon="importUpload" size={3} />
                                <Typography variant="body2">
                                    {t("puck.media_manager.select_file", "Click to select a file from media library")}
                                </Typography>
                                {(allowedExtensions || allowedMimeTypes) && (
                                    <Typography variant="caption" color="text.secondary">
                                        {allowedExtensions &&
                                            t("puck.media_manager.allowed_extensions", "Allowed: {{extensions}}", {
                                                extensions: allowedExtensions.join(", "),
                                            })}
                                        {allowedMimeTypes &&
                                            t("puck.media_manager.allowed_types", "Types: {{types}}", {
                                                types: allowedMimeTypes.join(", "),
                                            })}
                                    </Typography>
                                )}
                            </Stack>
                        }
                    </Box>

                    {isMediaSelected && (
                        <Button
                            variant="ternary"
                            size="small"
                            onClick={handleClearSelection}
                            startIcon={<FoodsIcon icon="close" size={2} />}
                        >
                            {t("puck.media_manager.action.clear_selection", "Clear Selection")}
                        </Button>
                    )}
                </Stack>
            )}

            <MediaManagerDialog
                open={isMediaDialogOpen}
                onClose={handleCloseMediaDialog}
                onSelect={handleMediaSelect}
                allowedMimeTypes={allowedMimeTypes}
                allowedExtensions={allowedExtensions}
                title={t("puck.media_manager.title", "Select Media")}
                showUploadButton={true}
                fullWidth={true}
                maxWidth="lg"
            />
        </Box>
    );
};

export default MediaManagerField;
